import React, { useState, useEffect, useRef } from "react";
import { database } from "../../firebaseConfig";
import { ref, get } from "firebase/database";
import { useActiveWorld } from "../../hooks/useActiveWorld";
import AddOfferServices from "./AddOfferServices";
import OfferMaker from "./OfferMaker";
import OfferDashboard from "./OfferDashboard";
import MicroservicesCreator from "./MicroservicesCreator/index";
import TeamSettings from "./TeamSettings";
import Contracts from "./Contracts";
import ContractMaker from "./ContractMaker";
import ValueBasedOffers from "./ValueBasedOffers";
import Terms from "./Terms";
import DropdownMenu from "./DropdownMenu";
import "./OfferCreator.css";

// Everything besides the dashboard is a building block you configure once and
// then reuse — so it lives behind a single "Library" menu instead of competing
// with the dashboard for space in a seven-item tab bar.
const LIBRARY_TABS = [
  {
    id: "services",
    label: "Services",
    icon: "fa-box",
    description: "Your billable services with prices and discounts",
  },
  {
    id: "microservices",
    label: "Microservices",
    icon: "fa-shapes",
    description: "Smaller building blocks you combine into services",
  },
  {
    id: "team",
    label: "Members",
    icon: "fa-user-group",
    description: "Contacts that appear as senders on your documents",
  },
  {
    id: "contracts",
    label: "Contract Templates",
    icon: "fa-file-contract",
    description: "Reusable contract layouts and default clauses",
  },
  {
    id: "valueBased",
    label: "Value Sets",
    icon: "fa-gem",
    description: "Option catalogue the AI draws from for value-based offers",
  },
  {
    id: "terms",
    label: "Terms",
    icon: "fa-scroll",
    description: "Legal texts you attach to offers and contracts",
  },
];

// Main component OfferCreator
const OfferCreator = ({ offerIdFromUrl = null, onNavigate = () => {} }) => {
  // Alle Angebote, Verträge, Services etc. hängen an der AKTIVEN World, nicht
  // am eingeloggten User. Team-Member mit Full Access arbeiten dadurch in der
  // World des Owners statt in ihrer eigenen, leeren.
  const {
    userId,
    isLoading: isWorldLoading,
    isSwitching,
  } = useActiveWorld();
  const [activeTab, setActiveTab] = useState(offerIdFromUrl ? "offers" : "dashboard"); // "dashboard", "services", "microservices", "team", "contracts" or "offers"
  const [newOfferData, setNewOfferData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newContractData, setNewContractData] = useState(null);
  const [isContractEditMode, setIsContractEditMode] = useState(false);

  const isLoading = isWorldLoading || isSwitching;

  // Ein Account-Wechsel über den TeamSwitcher im Header darf keinen Editor mit
  // Daten der vorherigen World stehen lassen — sonst würde ein Speichern das
  // fremde Angebot in die neue World schreiben.
  const previousWorldIdRef = useRef(null);
  useEffect(() => {
    const previousWorldId = previousWorldIdRef.current;
    previousWorldIdRef.current = userId;
    if (!previousWorldId || previousWorldId === userId) return;

    setNewOfferData(null);
    setIsEditMode(false);
    setNewContractData(null);
    setIsContractEditMode(false);
    setActiveTab("dashboard");
    if (offerIdFromUrl) onNavigate("/deals");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Load offer from URL parameter if available
  useEffect(() => {
    const loadOfferFromUrl = async () => {
      console.log("🔍 loadOfferFromUrl called:", { offerIdFromUrl, userId });
      
      if (offerIdFromUrl && userId) {
        try {
          const dbPath = `/World/${userId}/offers/${offerIdFromUrl}`;
          console.log("📂 Loading offer from path:", dbPath);
          
          const offerRef = ref(database, dbPath);
          const snapshot = await get(offerRef);
          
          console.log("📦 Snapshot exists:", snapshot.exists());
          console.log("📦 Raw snapshot.val():", JSON.stringify(snapshot.val(), null, 2));
          
          if (snapshot.exists()) {
            const offerData = snapshot.val();
            console.log("✅ Offer data loaded:", offerData);
            console.log("✅ Offer data keys:", Object.keys(offerData || {}));
            // Set offer data with all fields including documentType, invoiceNumber, invoiceDate
            const dataToSet = {
              id: offerIdFromUrl,
              ...offerData
            };
            console.log("📤 Setting newOfferData:", dataToSet.title);
            setNewOfferData(dataToSet);
            setIsEditMode(true);
            setActiveTab("offers");
            console.log("✅ State updated: isEditMode=true, activeTab=offers");
          } else {
            console.error("❌ Offer not found at path:", dbPath);
            // Navigate back to dashboard if offer doesn't exist
            setActiveTab("dashboard");
          }
        } catch (error) {
          console.error("❌ Error loading offer:", error);
          setActiveTab("dashboard");
        }
      }
    };

    loadOfferFromUrl();
  }, [offerIdFromUrl, userId]);

  // Create new offer and open for editing. `documentType` lets the dashboard
  // start the builder straight in invoice mode instead of making the user
  // find the toggle inside the form.
  const handleCreateOffer = (documentType = "offer") => {
    const isInvoice = documentType === "invoice";
    const defaultOfferData = {
      title: isInvoice ? "New Invoice" : "New Offer",
      documentType: isInvoice ? "invoice" : "offer",
      description: "",
      services: [],
      notes: "",
      logo: "",
      createdAt: Date.now(),
    };

    setNewOfferData(defaultOfferData);
    setIsEditMode(false);
    setActiveTab("offers");
  };

  // Edit existing offer
  const handleEditOffer = (offerData) => {
    setNewOfferData(offerData);
    setIsEditMode(true);
    setActiveTab("offers");
  };

  // Create new contract
  const handleCreateContract = () => {
    setNewContractData(null);
    setIsContractEditMode(false);
    setActiveTab("contractMaker");
  };

  // Edit existing contract
  const handleEditContract = (contractData) => {
    setNewContractData(contractData);
    setIsContractEditMode(true);
    setActiveTab("contractMaker");
  };

  const goToDashboard = () => {
    setActiveTab("dashboard");
    if (offerIdFromUrl) onNavigate("/deals");
  };

  const activeLibraryTab = LIBRARY_TABS.find((tab) => tab.id === activeTab) || null;
  const isEditorTab = activeTab === "offers" || activeTab === "contractMaker";
  const editorContext = !isEditorTab
    ? null
    : activeTab === "contractMaker"
      ? isContractEditMode
        ? "Editing contract"
        : "New contract"
      : isEditMode
        ? "Editing offer"
        : newOfferData?.documentType === "invoice"
          ? "New invoice"
          : "New offer";

  // Display for non-logged-in users
  if (!userId && !isLoading) {
    return <p className="offer-creator__error">Please log in to manage your offers.</p>;
  }

  return (
    <div className="offer-creator">
      {isLoading ? (
        <div className="offer-creator__loading">
          <div className="offer-creator__spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <>
          <div className="offer-creator__nav">
            <button
              type="button"
              className={`offer-creator__nav-home ${!activeLibraryTab ? "is-active" : ""}`}
              onClick={goToDashboard}
            >
              <span className="offer-creator__nav-icon" aria-hidden="true">
                <i className={`fas ${isEditorTab ? "fa-arrow-left" : "fa-table-cells-large"}`}></i>
              </span>
              Offers
            </button>

            {editorContext && (
              <span className="offer-creator__nav-context">
                <span className="offer-creator__nav-context-chevron" aria-hidden="true">
                  <i className="fas fa-chevron-right"></i>
                </span>
                {editorContext}
              </span>
            )}

            <span className="offer-creator__nav-spacer" />

            <DropdownMenu
              align="end"
              width={310}
              ariaLabel="Open library and settings"
              triggerClassName={`offer-creator__nav-library ${activeLibraryTab ? "is-active" : ""}`}
              triggerContent={
                <>
                  <span className="offer-creator__nav-icon" aria-hidden="true">
                    <i className={`fas ${activeLibraryTab ? activeLibraryTab.icon : "fa-sliders"}`}></i>
                  </span>
                  {activeLibraryTab ? activeLibraryTab.label : "Library"}
                  <span className="offer-creator__nav-caret" aria-hidden="true">
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </>
              }
              items={LIBRARY_TABS.map((tab) => ({
                id: tab.id,
                label: tab.label,
                description: tab.description,
                icon: tab.icon,
                active: activeTab === tab.id,
                onSelect: () => {
                  setActiveTab(tab.id);
                  if (offerIdFromUrl) onNavigate("/deals");
                },
              }))}
            />
          </div>
          
          <div className="offer-creator__content">
            {activeTab === "dashboard" && (
              <OfferDashboard 
                userId={userId} 
                onCreateOffer={handleCreateOffer}
                onEditOffer={handleEditOffer}
                onCreateContract={handleCreateContract}
                onEditContract={handleEditContract}
              />
            )}
            {activeTab === "services" && <AddOfferServices userId={userId} />}
            {activeTab === "microservices" && <MicroservicesCreator userId={userId} />}
            {activeTab === "team" && <TeamSettings userId={userId} />}
            {activeTab === "contracts" && <Contracts userId={userId} />}
            {activeTab === "valueBased" && <ValueBasedOffers userId={userId} />}
            {activeTab === "terms" && <Terms userId={userId} />}
            {activeTab === "offers" && (
              <OfferMaker 
                userId={userId} 
                initialOfferData={newOfferData}
                isEditMode={isEditMode}
                onBack={goToDashboard}
                onOfferSaved={() => {
                  setNewOfferData(null);
                  setIsEditMode(false);
                  setActiveTab("dashboard");
                  // Update URL to remove the offer ID
                  onNavigate("/deals");
                }}
              />
            )}
            {activeTab === "contractMaker" && (
              <ContractMaker 
                userId={userId} 
                initialContractData={newContractData}
                isEditMode={isContractEditMode}
                onContractSaved={() => {
                  setNewContractData(null);
                  setIsContractEditMode(false);
                  setActiveTab("dashboard");
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OfferCreator; 